import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency } from '../../utils/formatters';
import { Settings, ChevronRight, ChevronLeft, TrendingUp, DollarSign, BarChart3, Plus, Minus, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import ChartSeriesToggleCard from '../ui/ChartSeriesToggleCard';
import { getLanguageContent, getLanguageFromCurrency } from '../../data/languages';

interface TraditionalSpecificDataProps {
  onNext: () => void;
  onPrevious: () => void;
}

const TraditionalSpecificData: React.FC<TraditionalSpecificDataProps> = ({ onNext, onPrevious }) => {
  const { investment, updateInvestment, toggleLemonPrice, toggleTaxes } = useCalculator();

  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  // Handlers for increment/decrement buttons
  const handleIncrementInflation = () => {
    if (investment.inflationRate < 15) {
      updateInvestment({ inflationRate: Math.round((investment.inflationRate + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementInflation = () => {
    if (investment.inflationRate > 0) {
      updateInvestment({ inflationRate: Math.round((investment.inflationRate - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementLemonPrice = () => {
    if (investment.lemonPriceIncrease < 10) {
      updateInvestment({ lemonPriceIncrease: Math.round((investment.lemonPriceIncrease + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementLemonPrice = () => {
    if (investment.lemonPriceIncrease > 0) {
      updateInvestment({ lemonPriceIncrease: Math.round((investment.lemonPriceIncrease - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementTaxRate = () => {
    if (investment.taxRate < 50) {
      updateInvestment({ taxRate: investment.taxRate + 1 });
    }
  };

  const handleDecrementTaxRate = () => {
    if (investment.taxRate > 0) {
      updateInvestment({ taxRate: investment.taxRate - 1 });
    }
  };

  const handleIncrementCetesRate = () => {
    if (investment.cetesRate < 15) {
      updateInvestment({ cetesRate: Math.round((investment.cetesRate + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementCetesRate = () => {
    if (investment.cetesRate > 1) {
      updateInvestment({ cetesRate: Math.round((investment.cetesRate - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementSavingsRate = () => {
    if (investment.savingsRate < 15) {
      updateInvestment({ savingsRate: Math.round((investment.savingsRate + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementSavingsRate = () => {
    if (investment.savingsRate > 1) {
      updateInvestment({ savingsRate: Math.round((investment.savingsRate - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementRealEstateAppreciation = () => {
    if (investment.realEstateAppreciation < 15) {
      updateInvestment({ realEstateAppreciation: Math.round((investment.realEstateAppreciation + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementRealEstateAppreciation = () => {
    if (investment.realEstateAppreciation > 1) {
      updateInvestment({ realEstateAppreciation: Math.round((investment.realEstateAppreciation - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementRealEstateRent = () => {
    if (investment.realEstateRent < 15) {
      updateInvestment({ realEstateRent: Math.round((investment.realEstateRent + 0.5) * 10) / 10 });
    }
  };

  const handleDecrementRealEstateRent = () => {
    if (investment.realEstateRent > 0) {
      updateInvestment({ realEstateRent: Math.round((investment.realEstateRent - 0.5) * 10) / 10 });
    }
  };

  const handleIncrementEbitdaFactor = () => {
    if (investment.ebitdaFactor < 20) {
      updateInvestment({ ebitdaFactor: investment.ebitdaFactor + 1 });
    }
  };

  const handleDecrementEbitdaFactor = () => {
    if (investment.ebitdaFactor > 1) {
      updateInvestment({ ebitdaFactor: investment.ebitdaFactor - 1 });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Settings className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{content.traditional.specificData.title}</h2>
              <p className="text-blue-100">{content.traditional.specificData.subtitle}</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Inflation Rate */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.specificData.inflationRate.title}</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementInflation}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.inflationRate <= 0}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="0"
                  max="15"
                  step="0.5"
                  value={investment.inflationRate}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 15) {
                      updateInvestment({ inflationRate: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title={currentLanguage === 'es' ? 
                    "Tasa de inflación anual esperada. Afecta el poder adquisitivo real de tu inversión. En México históricamente oscila entre 3-6% anual" :
                    currentLanguage === 'en' ?
                    "Expected annual inflation rate. Affects the real purchasing power of your investment. In Mexico it historically ranges between 3-6% annually" :
                    "Taux d'inflation annuel attendu. Affecte le pouvoir d'achat réel de votre investissement. Au Mexique, il oscille historiquement entre 3-6% par an"}
                />
                <div className="text-sm text-orange-600">
                  {content.traditional.specificData.inflationRate.label}
                </div>
              </div>
              <button 
                onClick={handleIncrementInflation}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.inflationRate >= 15}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="0"
                max="15"
                step="0.5"
                value={investment.inflationRate}
                onChange={(e) => updateInvestment({ inflationRate: parseFloat(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-orange-200 to-orange-300 rounded-lg appearance-none cursor-pointer"
                title={currentLanguage === 'es' ? 
                  "Desliza para ajustar la inflación. Valores típicos: 3-5% estable, 6-8% moderada, 9%+ alta. Afecta los valores reales de tu inversión" :
                  currentLanguage === 'en' ?
                  "Slide to adjust inflation. Typical values: 3-5% stable, 6-8% moderate, 9%+ high. Affects the real values of your investment" :
                  "Glissez pour ajuster l'inflation. Valeurs typiques: 3-5% stable, 6-8% modérée, 9%+ élevée. Affecte les valeurs réelles de votre investissement"}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>15%</span>
              </div>
            </div>
          </div>

          {/* Lemon Price Increase */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{content.traditional.specificData.lemonPriceIncrease.title}</h3>
              </div>
              <ChartSeriesToggleCard
                label=""
                icon={TrendingUp}
                color="#f59e0b"
                checked={investment.increaseLemonPrice}
                onClick={toggleLemonPrice}
                className="w-32"
              />
            </div>
            
            {investment.increaseLemonPrice && (
              <>
                <div className="flex items-center justify-center gap-4 mb-5">
                  <button 
                    onClick={handleDecrementLemonPrice}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.lemonPriceIncrease <= 0}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.5"
                      value={investment.lemonPriceIncrease}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 10) {
                          updateInvestment({ lemonPriceIncrease: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                      title={currentLanguage === 'es' ? 
                        "Incremento anual del precio del limón a partir del año 6. Basado en tendencias históricas del mercado internacional de cítricos" :
                        currentLanguage === 'en' ?
                        "Annual increase in lemon price starting from year 6. Based on historical trends in the international citrus market" :
                        "Augmentation annuelle du prix du citron à partir de l'année 6. Basé sur les tendances historiques du marché international des agrumes"}
                    />
                    <div className="text-sm text-yellow-600">
                      {content.traditional.specificData.lemonPriceIncrease.label}
                    </div>
                  </div>
                  <button 
                    onClick={handleIncrementLemonPrice}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.lemonPriceIncrease >= 10}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={investment.lemonPriceIncrease}
                    onChange={(e) => updateInvestment({ lemonPriceIncrease: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gradient-to-r from-yellow-200 to-yellow-300 rounded-lg appearance-none cursor-pointer"
                    title={currentLanguage === 'es' ? 
                      "Desliza para ajustar el incremento. Histórico: 3-5% conservador, 5-7% moderado, 8-10% optimista. Se aplica solo después del año 5" :
                      currentLanguage === 'en' ?
                      "Slide to adjust the increase. Historical: 3-5% conservative, 5-7% moderate, 8-10% optimistic. Applied only after year 5" :
                      "Glissez pour ajuster l'augmentation. Historique: 3-5% conservateur, 5-7% modéré, 8-10% optimiste. Appliqué seulement après l'année 5"}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>10%</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Taxes */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{content.traditional.specificData.taxes.title}</h3>
              </div>
              <ChartSeriesToggleCard
                label=""
                icon={DollarSign}
                color="#dc2626"
                checked={investment.applyTaxes}
                onClick={toggleTaxes}
                className="w-32"
              />
            </div>
            
            {investment.applyTaxes && (
              <>
                <div className="flex items-center justify-center gap-4 mb-5">
                  <button 
                    onClick={handleDecrementTaxRate}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.taxRate <= 0}
                  >
                    <Minus className="w-5 h-5 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="1"
                      value={investment.taxRate}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value >= 0 && value <= 50) {
                          updateInvestment({ taxRate: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                      title={currentLanguage === 'es' ? 
                        "Tasa de impuestos aplicada a rendimientos de inversiones tradicionales (CETES, Ahorro, Bienes Raíces). No aplica a Cosecha Capital durante maduración" :
                        currentLanguage === 'en' ?
                        "Tax rate applied to traditional investment returns (CETES, Savings, Real Estate). Does not apply to Cosecha Capital during maturation" :
                        "Taux d'imposition appliqué aux rendements des investissements traditionnels (CETES, Épargne, Immobilier). Ne s'applique pas à Cosecha Capital pendant la maturation"}
                    />
                    <div className="text-sm text-red-600">
                      {content.traditional.specificData.taxes.taxRate}
                    </div>
                  </div>
                  <button 
                    onClick={handleIncrementTaxRate}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.taxRate >= 50}
                  >
                    <Plus className="w-5 h-5 text-gray-700" />
                  </button>
                </div>

                <div className="space-y-3">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    value={investment.taxRate}
                    onChange={(e) => updateInvestment({ taxRate: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gradient-to-r from-red-200 to-red-300 rounded-lg appearance-none cursor-pointer"
                    title={currentLanguage === 'es' ? 
                      "Desliza para ajustar impuestos. En México: ISR 30% típico, 35% alto. Solo afecta inversiones tradicionales, no a Cosecha Capital" :
                      currentLanguage === 'en' ?
                      "Slide to adjust taxes. In Mexico: ISR 30% typical, 35% high. Only affects traditional investments, not Cosecha Capital" :
                      "Glissez pour ajuster les impôts. Au Mexique: ISR 30% typique, 35% élevé. Affecte seulement les investissements traditionnels, pas Cosecha Capital"}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0%</span>
                    <span>50%</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Comparative Investment Rates */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.specificData.comparativeRates.title}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CETES Rate */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">{content.traditional.specificData.comparativeRates.cetes}</label>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button 
                    onClick={handleDecrementCetesRate}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.cetesRate <= 1}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="1"
                      max="15"
                      step="0.5"
                      value={investment.cetesRate}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 15) {
                          updateInvestment({ cetesRate: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-2xl font-bold text-blue-600 border-0 focus:ring-0 focus:outline-none"
                      title={currentLanguage === 'es' ? 
                        "Rendimiento anual de CETES (Certificados de la Tesorería). Instrumento gubernamental de bajo riesgo. Históricamente 6-10% anual" :
                        currentLanguage === 'en' ?
                        "Annual return of CETES (Treasury Certificates). Low-risk government instrument. Historically 6-10% annually" :
                        "Rendement annuel des CETES (Certificats du Trésor). Instrument gouvernemental à faible risque. Historiquement 6-10% par an"}
                    />
                    <span className="text-blue-600">%</span>
                  </div>
                  <button 
                    onClick={handleIncrementCetesRate}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.cetesRate >= 15}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="0.5"
                  value={investment.cetesRate}
                  onChange={(e) => updateInvestment({ cetesRate: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                  title={currentLanguage === 'es' ? 
                    "Desliza para ajustar CETES. Referencia: Banco de México publica tasas semanalmente. Típicamente 1-3% por debajo de la tasa de referencia" :
                    currentLanguage === 'en' ?
                    "Slide to adjust CETES. Reference: Bank of Mexico publishes rates weekly. Typically 1-3% below the reference rate" :
                    "Glissez pour ajuster CETES. Référence: La Banque du Mexique publie les taux hebdomadairement. Typiquement 1-3% en dessous du taux de référence"}
                />
              </div>

              {/* Savings Rate */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">{content.traditional.specificData.comparativeRates.savings}</label>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button 
                    onClick={handleDecrementSavingsRate}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.savingsRate <= 1}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="1"
                      max="15"
                      step="0.5"
                      value={investment.savingsRate}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 15) {
                          updateInvestment({ savingsRate: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-2xl font-bold text-purple-600 border-0 focus:ring-0 focus:outline-none"
                      title="Rendimiento anual de cuentas de ahorro tradicionales en bancos. Generalmente muy bajo, 2-6% anual antes de inflación"
                    />
                    <span className="text-purple-600">%</span>
                  </div>
                  <button 
                    onClick={handleIncrementSavingsRate}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.savingsRate >= 15}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="0.5"
                  value={investment.savingsRate}
                  onChange={(e) => updateInvestment({ savingsRate: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer"
                  title="Desliza para ajustar ahorro tradicional. Incluye cuentas de ahorro, pagarés bancarios y instrumentos similares de bajo riesgo"
                />
              </div>

              {/* Real Estate Appreciation */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">{content.traditional.specificData.comparativeRates.realEstate}</label>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <button 
                    onClick={handleDecrementRealEstateAppreciation}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.realEstateAppreciation <= 1}
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </button>
                  <div className="text-center">
                    <input
                      type="number"
                      min="1"
                      max="15"
                      step="0.5"
                      value={investment.realEstateAppreciation}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 15) {
                          updateInvestment({ realEstateAppreciation: value });
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="w-20 text-center text-2xl font-bold text-orange-600 border-0 focus:ring-0 focus:outline-none"
                      title="Apreciación anual de bienes raíces más rendimiento por renta. Combina plusvalía inmobiliaria y ingresos por arrendamiento"
                    />
                    <span className="text-orange-600">%</span>
                  </div>
                  <button 
                    onClick={handleIncrementRealEstateAppreciation}
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                    disabled={investment.realEstateAppreciation >= 15}
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </button>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="0.5"
                  value={investment.realEstateAppreciation}
                  onChange={(e) => updateInvestment({ realEstateAppreciation: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gradient-to-r from-orange-200 to-orange-300 rounded-lg appearance-none cursor-pointer"
                  title="Desliza para ajustar bienes raíces. Incluye apreciación del inmueble + rendimiento por renta. Históricamente 5-12% anual en México"
                />
              </div>
            </div>
          </div>
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onPrevious}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>{content.common.buttons.previous}</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>{content.common.buttons.continue}</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TraditionalSpecificData;